import { fetchWithTrace } from './fetchWithTrace';

const getUserId = () => localStorage.getItem('userId') || 'guest';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const addProject = async (project) => {
  const userId = getUserId();

  try {
    const { data } = await fetchWithTrace(`${API_URL}/projects`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: {
        userId: userId,
        title: project.title,
        description: project.story,
      }
    });
    return data.project_id;
  } catch (error) {
    console.error('Error adding project:', error);
    // Log the actual backend error info if available
    if (error.body) console.error('Backend error details:', error.body);
    throw new Error(error.message || 'Project creation failed');
  }
};

export const deleteProject = async (projectId) => {
  try {
    await fetchWithTrace(`${API_URL}/projects/${projectId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    console.log(`Project ${projectId} deleted successfully.`);
  } catch (error) {
    console.error('Error deleting project:', error);
    throw error;
  }
};

export const getProjects = async () => {
  const userId = localStorage.getItem('userId');
  const token = localStorage.getItem('token');

  try {
    const { data } = await fetchWithTrace(`${API_URL}/projects/${userId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return data;
  } catch (error) {
    if (error.status === 404) {
      console.warn("No projects found for this user.");
      return [];
    }
    console.error('Error fetching projects:', error);
    throw error;
  }
};

export const getModels = async () => {
  try {
    const { data } = await fetchWithTrace(`${API_URL}/api/models`, {
      method: 'GET'
    });
    return data;
  } catch (error) {
    console.warn('Failed to fetch models, using defaults', error);
    return null;
  }
};

export const saveScreenplay = async (projectId, screenplay) => {
  try {
    const { data } = await fetchWithTrace(`${API_URL}/screenplays`, {
      method: 'POST',
      body: {
        projectId: projectId,
        screenplay: screenplay
      }
    });
    return data.screenplay_id;
  } catch (error) {
    console.error('Error saving screenplay:', error);
    throw new Error('Screenplay save failed');
  }
};

export const getScreenplay = async (projectId) => {
  try {
    const { data } = await fetchWithTrace(`${API_URL}/screenplays/${projectId}`, { method: 'GET' });
    return data.screenplay;
  } catch (error) {
    console.error('Error fetching screenplay:', error);
    throw new Error('Failed to retrieve screenplay');
  }
};

export const saveScreenplayOutline = async (projectId, outline) => {
  try {
    const { data } = await fetchWithTrace(`${API_URL}/screenplays/outline`, {
      method: 'POST',
      body: { projectId, outline }
    });
    return data.outline_id || null;
  } catch (e) {
    throw new Error('Failed to store screenplay outline in MongoDB');
  }
};

export const getScreenplayOutline = async (projectId) => {
  try {
    const { data } = await fetchWithTrace(`${API_URL}/screenplays/outline/${projectId}`, { method: 'GET' });
    return data.outline || null;
  } catch (e) {
    if (e.status === 404) return null;
    throw new Error('Failed to fetch screenplay outline from MongoDB');
  }
};

export const saveScenes = async (projectId, scenes) => {
  try {
    await fetchWithTrace(`${API_URL}/projects/${projectId}/scenes`, {
      method: 'POST',
      body: { scenes: scenes }
    });
  } catch (error) {
    console.error('Error saving scenes:', error);
    throw new Error('Scene save failed');
  }
};

export const getScenes = async (projectId) => {
  try {
    const { data } = await fetchWithTrace(`${API_URL}/projects/${projectId}/scenes`, { method: 'GET' });
    return data.scenes;
  } catch (error) {
    console.error('Error fetching scenes from MongoDB:', error);
  }
};

export const saveCharacterBible = async (projectId, characterBible) => {
  try {
    const characterBibleObject = mapToObject(characterBible);
    const { data } = await fetchWithTrace(`${API_URL}/characterbibles`, {
      method: 'POST',
      body: {
        projectId: projectId,
        characterBible: characterBibleObject
      }
    });
    return data.character_bible_id;
  } catch (error) {
    console.error('Error saving character bible:', error);
    throw new Error('Character bible save failed');
  }
};

export const getCharacterBible = async (projectId) => {
  try {
    const { data } = await fetchWithTrace(`${API_URL}/characterbibles/${projectId}`, { method: 'GET' });
    return objectToMap(data.characterBible);
  } catch (error) {
    console.error('Error fetching character bible:', error);
    throw new Error('Failed to retrieve character bible');
  }
};

export const saveImageOnCloud = async (image_url, image_id, projectId = null, folder = 'uploads') => {
  try {
    const { data } = await fetchWithTrace(`${API_URL}/upload-image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: {
        image_url: image_url,
        image_name: image_id,
        projectId,
        folder
      }
    });
    return data.url;
  } catch (error) {
    console.error('Error saving image to the cloud:', error);
    throw new Error('Failed to save image to the cloud');
  }
};

export const saveImageOnCloudB64 = async (b64_json, image_id, projectId = null, folder = 'uploads') => {
  try {
    const clean = (b64_json || '').split(',').pop().trim();
    const { data } = await fetchWithTrace(`${API_URL}/upload-image-b64`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: {
        b64_json: clean,
        image_name: image_id,
        projectId,
        folder
      }
    });
    return data.url;
  } catch (error) {
    console.error('Error saving image to the cloud:', error);
    throw new Error('Failed to save image to the cloud');
  }
};

export const saveVideoOnCloud = async (video_url, video_id, projectId = null, folder = 'uploads') => {
  try {
    const { data } = await fetchWithTrace(`${API_URL}/upload-video`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: {
        video_url: video_url,
        video_name: video_id,
        projectId,
        folder
      }
    });
    return data.url;
  } catch (error) {
    console.error('Error saving video to the cloud:', error);
    throw new Error('Failed to save video to the cloud');
  }
}

export const saveImageOnCloudB64e = async (b64_json, image_id, projectId = null, folder = 'uploads') => {
  try {
    // Convert base64 to a Blob in the browser to avoid server-side b64 decode
    const clean = (b64_json || '').split(',').pop().trim();
    const byteChars = atob(clean);
    const byteNums = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
    const byteArray = new Uint8Array(byteNums);

    // Best-effort MIME sniff (keeps file extensions right)
    let mime = 'image/jpeg';
    if (byteArray[0] === 0x89 && byteArray[1] === 0x50) mime = 'image/png';       // PNG
    else if (byteArray[0] === 0xFF && byteArray[1] === 0xD8) mime = 'image/jpeg'; // JPEG
    else if (byteArray[0] === 0x52 && byteArray[1] === 0x49 && byteArray[2] === 0x46 && byteArray[8] === 0x57) mime = 'image/webp';

    const blob = new Blob([byteArray], { type: mime });

    const form = new FormData();
    form.append('file', blob, image_id);  // filename hint
    form.append('image_name', image_id);
    if (projectId) form.append('projectId', projectId);
    form.append('folder', folder);

    // Use fetchWithTrace for FormData
    const { data } = await fetchWithTrace(`${API_URL}/upload-image-bytes`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: form,
      credentials: 'include',
    });

    return data.url;
  } catch (error) {
    console.error('Error saving image to the cloud:', error);
    throw new Error('Failed to save image to the cloud');
  }
};

export const saveAudioOnCloud = async (audioFile, audioName, projectId = null, folder = 'uploads') => {
  if (!(audioFile instanceof File)) {
    throw new Error("Invalid file input. Please provide a File object.");
  }

  // Prepare form data to send in the request
  const formData = new FormData();
  formData.append("file", audioFile);
  formData.append("audio_name", audioName);
  if (projectId) formData.append("projectId", projectId);
  formData.append("folder", folder);

  try {
    // Make the POST request to upload the audio file
    const { data } = await fetchWithTrace(`${API_URL}/upload-audio`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: formData,
    });

    return data.url;  // Return the URL of the uploaded audio

  } catch (error) {
    console.error("Failed to upload audio:", error);
    throw error;  // Rethrow error for further handling if needed
  }
}

export const mergeScenes = async (projectId, videoUrls, outputName) => {
  try {
    const { data } = await fetchWithTrace(`${API_URL}/merge-scenes`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: {
        projectId,
        videos: videoUrls,
        outputName,
      },
    });
    return data.url;
  } catch (error) {
    console.error('Error merging scenes:', error);
    throw error;
  }
};

export const updateWorkflowState = async (projectId, workflowState) => {
  await fetchWithTrace(`${API_URL}/projects/${projectId}/workflow`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    },
    body: { workflowState },
  });
  return true;
};

export const saveMergedVideoUrl = async (projectId, mergedVideoUrl) => {
  try {
    await fetchWithTrace(`${API_URL}/projects/${projectId}/merged-video`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: { mergedVideoUrl },
    });
    return true;
  } catch (error) {
    console.error('Error updating merged video URL:', error);
    throw error;
  }
};

const mapToObject = (map) => {
  const obj = {};
  for (const [key, value] of map.entries()) {
    obj[key] = value;
  }
  return obj;
};

const objectToMap = (obj) => {
  const map = new Map();
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      map.set(key, obj[key]);
    }
  }
  return map;
};
